import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { addNote } from '../../services/api/visitMetadataApi';
import { Z_WIDGET_OVERLAY } from '../../constants/zLayers';
import { DiagnosisFormProvider } from '../sdk/useDiagnosisFormField';

/**
 * DiagnosisWidget — role-agnostic "add a clinical diagnosis" form.
 *
 * Opened via `sdk.openWidget('diagnosis', context, opts)`. Renders as a
 * modal-style overlay at the Z_WIDGET_OVERLAY tier so it floats over
 * the chart pane (Z_CHART_PANE) without occluding the right-edge
 * widget rail (Z_WIDGET_RAIL, which sits between chart and widget
 * overlay numerically). The widget owns the form UX (severity +
 * textarea + Save / Cancel) and persists via the shared
 * `visitMetadataApi.addNote()` endpoint with `kind='diagnosis'`. After
 * a successful save the widget dispatches a window event
 * `arogya:widgets:diagnosis-saved` so chart plugins can fire their
 * specialty-specific side effects (optimistic insert, surface
 * auto-advance, etc.) without the widget needing to know about them.
 *
 * MVP scope notes
 * ---------------
 *  - Severity is generic enough (mild / moderate / severe) that it
 *    lives in the widget rather than as a per-specialty extension.
 *    Future specialties that don't use severity can hide the row via
 *    `opts.hideSeverity` (added when plugin #2 actually needs it).
 *  - `opts.extras` is reserved for specialty-specific inputs (e.g.
 *    dental common-diagnosis quick chips). For MVP, the dental plugin
 *    doesn't pass extras — the user types the diagnosis manually. The
 *    quick-chip UX is a minor UX regression we accept until the
 *    ExtensionSlot mechanism lands with plugin #2.
 *  - Anatomical surfaces are picked in the chart BEFORE the widget
 *    opens (the chart's tooth-click selects whole-tooth or per-surface
 *    tokens, e.g. `16-M`). The widget displays them via the summary
 *    chip; it does NOT let the user edit them.
 */

const SEVERITY_OPTIONS = [
  { value: 'mild',     label: 'Mild',     accent: '#ca8a04' },
  { value: 'moderate', label: 'Moderate', accent: '#ea580c' },
  { value: 'severe',   label: 'Severe',   accent: '#dc2626' },
];

export default function DiagnosisWidget({ context, opts, visit: _visit, viewer, onClose }) {
  const visitId = context?.visitId;
  const anatomicalRef = context?.anatomicalRef || null;
  const metaId = context?.metaId || null;        // reserved — edit flow lands in PR 5
  const extras = opts?.extras || null;
  const composePayload = opts?.composePayload || null;

  const initialContent = (context?.prefill?.content && String(context.prefill.content)) || '';
  const initialSeverity = (context?.prefill?.severity && String(context.prefill.severity)) || 'moderate';
  const initialSurfaces = Array.isArray(context?.prefill?.surfaces)
    ? context.prefill.surfaces
    : [];

  const [content, setContent] = useState(initialContent);
  const [severity, setSeverity] = useState(initialSeverity);
  // Phase 2.B — surfaces are now editable INSIDE the widget via the
  // dental `ToothDiagnosisExtras` extension. The widget owns the state;
  // the extension is the renderer. Non-dental callers ignore it.
  const [surfaces, setSurfaces] = useState(initialSurfaces);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | error
  const [errorMsg, setErrorMsg] = useState(null);

  const textareaRef = useRef(null);

  // Form-state context exposed to extras via the public
  // `useDiagnosisFormField(key)` hook. Adding a new key here is a v1
  // SDK extension — document in `useDiagnosisFormField.js`.
  const formCtxValue = useMemo(() => ({
    values: { content, severity, surfaces },
    set: (key, next) => {
      // React's useState setter accepts both values and updater
      // functions, so we forward `next` verbatim either way.
      if (key === 'content')       setContent(next);
      else if (key === 'severity') setSeverity(next);
      else if (key === 'surfaces') setSurfaces(next);
      else if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[widgets] useDiagnosisFormField('${key}') — unknown field`);
      }
    },
  }), [content, severity, surfaces]);

  useEffect(() => {
    // Auto-focus the textarea when the widget mounts so the dentist
    // can start typing without an extra tap. preventScroll keeps the
    // chart pane from jumping.
    try { textareaRef.current?.focus({ preventScroll: true }); }
    catch (_e) { /* old browsers */ }
  }, []);

  // ESC closes the widget — single keyboard escape across all widget kinds.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSave = content.trim().length > 0 && saveState !== 'saving' && !!visitId;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaveState('saving');
    setErrorMsg(null);
    try {
      const tokens = (anatomicalRef?.payload && Array.isArray(anatomicalRef.payload.tokens))
        ? anatomicalRef.payload.tokens
        : [];

      // Specialty-aware composition. Dental passes `composePayload`
      // to apply the surface prefix (`[M,O] caries...`) before save.
      // Non-dental callers omit it and we use the raw fields.
      const composed = typeof composePayload === 'function'
        ? composePayload({ content: content.trim(), severity, surfaces })
        : { content: content.trim(), severity };

      const finalContent  = composed.content  ?? content.trim();
      const finalSeverity = composed.severity ?? severity;

      const result = await addNote(visitId, finalContent, {
        toothNumbers: tokens.length > 0 ? tokens : null,
        kind: 'diagnosis',
        severity: finalSeverity,
      });
      // Side-channel for chart plugins — dental subscribes and pushes
      // the saved row into its optimistic-insert cache, then drives the
      // chart's "just-saved" pulse + surface auto-advance.
      try {
        window.dispatchEvent(new CustomEvent('arogya:widgets:diagnosis-saved', {
          detail: {
            result,
            visitId,
            anatomicalRef,
            severity: finalSeverity,
            surfaces,
            content: finalContent,
            viewer,
          },
        }));
      } catch (_e) { /* ignore */ }
      onClose?.();
    } catch (err) {
      setSaveState('error');
      setErrorMsg(err?.response?.data?.detail || err?.message || 'Failed to save');
    }
  }, [canSave, anatomicalRef, composePayload, content, severity, surfaces, visitId, viewer, onClose]);

  return (
    <div
      role="dialog"
      aria-label="Add diagnosis"
      aria-modal="true"
      style={{ zIndex: Z_WIDGET_OVERLAY }}
      className="fixed inset-0 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="w-full max-w-md rounded-xl shadow-2xl ring-1 ring-slate-200 dark:ring-slate-700
                   bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                   flex flex-col gap-3 p-4"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Add diagnosis</h3>
            {anatomicalRef?.summary && (
              <div
                className="mt-1 inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600
                           bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-700 dark:text-slate-200"
              >
                {anatomicalRef.summary}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200
                       hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1 text-[11px]">
          <span className="opacity-70 mr-1">Severity:</span>
          {SEVERITY_OPTIONS.map((opt) => {
            const active = severity === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSeverity(opt.value)}
                style={active
                  ? { backgroundColor: opt.accent, borderColor: opt.accent, color: '#ffffff' }
                  : undefined}
                className={[
                  'px-2 py-0.5 rounded-md border',
                  active
                    ? 'font-semibold'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700',
                ].join(' ')}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder={`Diagnosis${anatomicalRef?.summary ? ` on ${anatomicalRef.summary.toLowerCase()}` : ''}…`}
          className="w-full px-2 py-1.5 text-xs rounded-md
                     border border-slate-300 dark:border-slate-600
                     bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
        />

        {extras && (
          <div className="border-t border-slate-200 dark:border-slate-700 pt-2">
            <DiagnosisFormProvider value={formCtxValue}>
              {extras}
            </DiagnosisFormProvider>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="text-[11px] min-h-[16px]">
            {saveState === 'saving' && (
              <span className="text-slate-500 dark:text-slate-400">Saving…</span>
            )}
            {saveState === 'error' && (
              <span className="text-rose-600 dark:text-rose-300">{errorMsg || 'Save failed'}</span>
            )}
            {metaId && saveState === 'idle' && (
              <span className="text-slate-500 dark:text-slate-400">Editing existing diagnosis</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-2.5 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600
                         bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200
                         hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="px-2.5 py-1 text-xs font-semibold rounded-md text-white disabled:opacity-60
                         bg-rose-600 hover:bg-rose-700"
            >
              {saveState === 'saving' ? 'Saving…' : '+ Add diagnosis'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
